import { Badge, copyText, type BadgeVariant } from "@dl/web-core-js/ui";
import { useTranslation } from "react-i18next";
import type { DisplayCollectionImageLineDTO, S3ObjectDTO } from "../api";

/** An S3 object's URL, or null when the object is missing or archived. */
export function objectUrl(object: S3ObjectDTO | null | undefined): string | null {
  if (!object?.url || object.url.trim() === "") return null;
  if (object.status && object.status !== "ACTIVE") return null;
  return object.url;
}

/**
 * The S3 object behind a display-collection line.
 *
 * The server resolves this with a `@JsonIgnore` getter, so the wire carries
 * only `artwork_image` / `product_image` — the line has no `image` of its own
 * and reading one blanks every thumbnail on the screen.
 */
export function lineImage(line: DisplayCollectionImageLineDTO): S3ObjectDTO | null {
  const source = line.owner === "ARTWORK" ? line.artwork_image : line.product_image;
  return source?.image ?? null;
}

export function Thumbnail({
  url,
  alt = "",
  size = "sm",
  fallback = "NA",
}: {
  url: string | null | undefined;
  alt?: string;
  size?: "sm" | "md" | "lg";
  fallback?: string;
}) {
  const box = size === "lg" ? "size-16" : size === "md" ? "size-10" : "size-7";

  if (!url) {
    return (
      <span
        className={`${box} inline-flex items-center justify-center rounded border border-line bg-surface-sunk text-[10px] font-medium text-ink-faint`}
      >
        {fallback}
      </span>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      // A bucket object can 404 long after the row was written; an empty box
      // reads better than a broken-image glyph repeated down the column.
      onError={(event) => {
        event.currentTarget.style.visibility = "hidden";
      }}
      className={`${box} rounded border border-line object-cover`}
    />
  );
}

/** The `ListItem` equivalent: a bold primary line over a muted secondary one. */
export function TwoLine({
  primary,
  secondary,
}: {
  primary: string | number | null | undefined;
  secondary?: string | number | null | undefined;
}) {
  return (
    <span className="flex flex-col leading-tight">
      <span className="truncate font-medium text-ink">{primary ?? ""}</span>
      {secondary !== null && secondary !== undefined && secondary !== "" && (
        <span className="truncate text-[11px] text-ink-muted">{secondary}</span>
      )}
    </span>
  );
}

/** The "Copy URL" cell every image grid carries. */
export function CopyUrl({ url }: { url: string | null | undefined }) {
  const { t } = useTranslation();
  if (!url) return <span className="text-ink-faint">—</span>;
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        void copyText(
          url,
          t("notification.copy-clipboard.success", "Copied to clipboard"),
          t("notification.copy-clipboard.error", "Unable to copy to clipboard"),
        );
      }}
      className="text-accent hover:underline"
    >
      {t("value.copy-url", "Copy URL")}
    </button>
  );
}

/** An external link that opens in a new tab, used for order and product URLs. */
export function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="font-medium text-accent hover:underline"
    >
      {children}
    </a>
  );
}

export function EnumBadge<T extends string>({
  value,
  variants,
  size = "md",
}: {
  value: T | null | undefined;
  variants: Partial<Record<T, BadgeVariant>>;
  size?: "sm" | "md";
}) {
  if (!value) return <span className="text-ink-faint">—</span>;
  return (
    <Badge variant={variants[value] ?? "neutral"} size={size}>
      {value}
    </Badge>
  );
}

// ── formatting ────────────────────────────────────────────────────────────

export function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const MONEY = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function money(value: number | string | null | undefined): string {
  const amount = typeof value === "string" ? Number.parseFloat(value) : value;
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "";
  return MONEY.format(amount);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return `${value.toFixed(2)}%`;
}

/**
 * A product category's display name: the `getFullName()` join used everywhere
 * a category is shown in a picker or a two-line cell.
 */
export function categoryFullName(
  category: { name?: string | null; code?: string | null; label?: string | null; material?: string | null; color?: string | null } | null | undefined,
): string {
  if (!category) return "";
  return [category.name, category.code, category.label, category.material, category.color]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(", ");
}
